/* eslint-env browser */
import * as React from 'react';
import { AdyenCheckout, Dropin, Card, PayPal, ApplePay, SepaDirectDebit, Bancontact, BcmcMobile, Klarna, MBWay, Ach, Swish, Twint, AfterPay, WeChat } from '@adyen/adyen-web';
import { useDispatch, useSelector } from 'react-redux';
import { localeSelector, useTranslator } from '@gandi/translate';
import { Base } from '@gandi/ui';
import { useNotifications } from '../../../../hooks/notifications';
import { resource, preparePSPCall, selectPSPCallParams } from '../../../../modules/money';
var styles = {
  "root": "AdyenDropIn-root_1lkLD"
};
import { useAdyenLocale } from './useAdyenLocale';
import { jsx as _jsx } from "react/jsx-runtime";
const buildReturnUrl = (returnUrl, status) => {
  const url = new URL(returnUrl);
  url.searchParams.set('status', status);
  return url.href;
};
export default function AdyenDropIn({
  adyenDropinMean,
  order,
  onAdyenPaymentEvent,
  onSelect,
  onReady,
  onError,
  returnUrl,
  sharingId,
  context,
  dropinRef,
  shouldResetChoice
}) {
  const {
    __
  } = useTranslator();
  const {
    notifyMaybeApiError
  } = useNotifications();
  const dispatch = useDispatch();
  const [adyenDropin, setAdyenDropin] = React.useState({
    pay: undefined,
    flush: undefined,
    refill: undefined,
    recurrent: undefined
  });
  const locale = useSelector(localeSelector);
  const {
    translationCode
  } = useAdyenLocale({
    locale,
    countryCode: order.contact_infos.country_iso
  });
  const label = `dropin-${order.id}.${sharingId}.${context}`;
  const options = React.useMemo(() => ({
    label,
    context
  }), [label, context]);
  const pspCallParams = useSelector(state => selectPSPCallParams(state, options));
  const dropinCallParams = pspCallParams?.url === null && pspCallParams.params;
  const isCreatingOne = useSelector(state => resource.selectIsCreatingOne(state, {
    label
  }));
  const [apiFailure, setApiFailure] = React.useState(false);
  const onPaymentCompleted = React.useCallback(async ({
    resultCode
  }) => {
    let statusCode = 'failed';
    if (resultCode === 'Authorised') {
      statusCode = 'success';
    }
    await onAdyenPaymentEvent(statusCode);
    window?.location.assign(buildReturnUrl(returnUrl, statusCode));
  }, [onAdyenPaymentEvent, returnUrl]);
  const handleError = React.useCallback(error => {
    const {
      name
    } = error;
    onAdyenPaymentEvent(name === 'CANCEL' ? 'cancel' : 'error');
    dispatch(notifyMaybeApiError(error, error.message));
    if (onError) {
      onError(error);
    }
  }, [onAdyenPaymentEvent, onError, notifyMaybeApiError, dispatch]);
  const handleSubmit = React.useCallback((data, _, actions) => {
    onAdyenPaymentEvent('start');
    actions.resolve(data);
  }, [onAdyenPaymentEvent]);
  const shouldCreate = !dropinCallParams && !isCreatingOne && !apiFailure;
  React.useEffect(() => {
    if (shouldCreate) {
      try {
        dispatch(preparePSPCall({
          order,
          mean: adyenDropinMean,
          returnUrl,
          sharingId,
          locale,
          context,
          ...options
        }));
      } catch (e) {
        setApiFailure(true);
        dispatch(notifyMaybeApiError(e));
      }
    }
  }, [adyenDropinMean, context, dispatch, locale, notifyMaybeApiError, order, options, onSelect, returnUrl, sharingId, shouldCreate]);
  const loadAdyen = React.useCallback(async () => {
    const countryCode = order.contact_infos.country_iso;
    const amount = order.price_after_taxes;
    try {
      // $FlowFixMe[prop-missing]
      // $FlowFixMe[not-an-object]
      const {
        paymentMethodsConfiguration,
        ...restDropinCallParams
      } = dropinCallParams;
      // eslint-disable-next-line new-cap
      const checkout = await AdyenCheckout({
        ...restDropinCallParams,
        locale: translationCode,
        countryCode,
        amount,
        onPaymentCompleted,
        onPaymentFailed: onPaymentCompleted,
        onError: handleError,
        beforeSubmit: handleSubmit
      });
      if (dropinRef.current !== null) {
        const dropin = new Dropin(checkout, {
          openFirstPaymentMethod: false,
          onSelect: e => {
            // workaround since we cannot display the applepay button
            if (e.props.type === 'applepay') {
              dropin.closeActivePaymentMethod();
              e.submit();
            }
            return onSelect();
          },
          onReady,
          paymentMethodsConfiguration: {
            ...paymentMethodsConfiguration,
            wechatpayQR: {
              name: 'WeChat Pay QR'
            }
          },
          paymentMethodComponents: [Card, PayPal, ApplePay, SepaDirectDebit, Bancontact, BcmcMobile, Klarna, MBWay, Ach, Swish, AfterPay, Twint, WeChat],
          showRadioButton: true,
          disableFinalAnimation: true
        });
        setAdyenDropin({
          ...adyenDropin,
          [String(context)]: dropin
        });
        dropin.mount(dropinRef.current);
      }
    } catch (err) {
      dispatch(notifyMaybeApiError(err, __('Unable to load payment methods. Please retry later.')));
    }
  }, [__, translationCode, adyenDropin, context, dropinCallParams, dropinRef, handleSubmit, notifyMaybeApiError, handleError, onPaymentCompleted, onSelect, order, dispatch, onReady]);
  React.useEffect(() => {
    if (dropinCallParams && !adyenDropin[context]) {
      loadAdyen();
    }
  }, [loadAdyen, adyenDropin, context, dropinRef, dropinCallParams]);
  React.useEffect(() => {
    if (adyenDropin[context]) {
      adyenDropin[context].dropinRef.closeActivePaymentMethod();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldResetChoice]);
  return /*#__PURE__*/_jsx(Base, {
    className: styles.root,
    innerRef: dropinRef,
    marginBottom: "xsmall"
  });
}